import React, { useEffect, useState } from 'react';
import { FaArrowRight, FaFacebookF, FaInstagram, FaTiktok, FaYoutube } from 'react-icons/fa';
import { HiOutlineHeart } from 'react-icons/hi2';
import { Link } from 'react-router-dom';
import { fetchEvents } from '../api/events';
import './css/Home.css';

const imageUrl = (fileName) => `https://aqgeoubahtnbzgfglgky.supabase.co/storage/v1/object/public/images/${fileName}`;
const videoUrl = (fileName) => `https://aqgeoubahtnbzgfglgky.supabase.co/storage/v1/object/public/videos/${fileName}`;

const featureLinks = [
    {
        title: 'Ken & Bryn - Exclusive Members-Only Content',
        description: 'Join for exclusive members-only content, including full-length dance performances, behind-the-scenes footage, and more.',
        href: 'https://members.kenandbryn.dance',
        thumbnail: imageUrl('dance-performance.jpeg'),
        external: true,
    },
    {
        title: 'Follow Our Story on Ko-fi',
        href: 'https://gift.kenandbryn.dance',
        icon: <HiOutlineHeart />,
        thumbnail: imageUrl('ken-bryn-profile.jpeg'),
        external: true,
    },
];

const socialLinks = [
    {
        label: 'YouTube',
        href: 'https://www.youtube.com/channel/UCDPPfrCYMXon-KCSRfKymTQ',
        icon: <FaYoutube />,
    },
    {
        label: 'Instagram',
        href: 'https://www.instagram.com/kenandbryn/',
        icon: <FaInstagram />,
    },
    {
        label: 'TikTok',
        href: 'https://www.tiktok.com/@kenandbryn',
        icon: <FaTiktok />,
    },
    {
        label: 'Facebook',
        href: 'https://www.facebook.com/kenandbryn/',
        icon: <FaFacebookF />,
    },
];

const formatEventLocation = (location) => {
    if (typeof location === 'string') return location;
    if (!location) return '';
    return [location.city, location.state, location.country].filter(Boolean).join(', ');
};

const formatEventDate = (startDate, endDate) => {
    const start = new Date(`${startDate}T00:00:00Z`);
    const end = new Date(`${endDate}T00:00:00Z`);
    const month = start.toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' }).toUpperCase();
    const startDay = start.getUTCDate();
    const endDay = end.getUTCDate();
    const sameMonth = start.getUTCMonth() === end.getUTCMonth() && start.getUTCFullYear() === end.getUTCFullYear();

    return {
        month,
        days: startDay === endDay
            ? `${startDay}`
            : sameMonth
                ? `${startDay}-${endDay}`
                : `${startDay}-${end.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })}`,
    };
};

const Home = () => {
    const [scrollProgress, setScrollProgress] = useState(0);
    const [isHeroVideoLoaded, setIsHeroVideoLoaded] = useState(false);
    const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
    const [upcomingEvents, setUpcomingEvents] = useState(null);

    useEffect(() => {
        let active = true;
        fetchEvents()
            .then((events) => {
                if (active) setUpcomingEvents(events.slice(0, 3));
            })
            .catch(() => {
                if (active) setUpcomingEvents([]);
            });
        return () => { active = false; };
    }, []);

    useEffect(() => {
        const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        let idleCallbackId;
        let fallbackTimerId;

        if (reducedMotion?.matches || connection?.saveData) {
            return undefined;
        }

        const enableVideo = () => {
            if (window.requestIdleCallback) {
                idleCallbackId = window.requestIdleCallback(() => setShouldLoadVideo(true), { timeout: 2000 });
            } else {
                fallbackTimerId = window.setTimeout(() => setShouldLoadVideo(true), 500);
            }
        };

        if (document.readyState === 'complete') {
            enableVideo();
        } else {
            window.addEventListener('load', enableVideo, { once: true });
        }

        return () => {
            window.removeEventListener('load', enableVideo);
            if (idleCallbackId !== undefined) window.cancelIdleCallback?.(idleCallbackId);
            if (fallbackTimerId !== undefined) window.clearTimeout(fallbackTimerId);
        };
    }, []);

    useEffect(() => {
        const updateScrollProgress = () => {
            const progress = Math.min(window.scrollY / 280, 1);
            setScrollProgress(progress);
        };

        updateScrollProgress();
        window.addEventListener('scroll', updateScrollProgress, { passive: true });

        return () => window.removeEventListener('scroll', updateScrollProgress);
    }, []);

    const heroStyle = {
        '--profile-image-opacity': Math.max(1 - scrollProgress * 1.08, 0),
    };

    return (
        <main className={`links-page${isHeroVideoLoaded ? ' links-page-video-ready' : ''}`}>
            {shouldLoadVideo && (
                <video
                    className="links-background-video"
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    aria-hidden="true"
                    onLoadedData={() => setIsHeroVideoLoaded(true)}
                >
          <source src={videoUrl('social-hero.mp4')} type="video/mp4" />
                </video>
            )}

            <div className="links-hero" style={heroStyle}>
                <section className="links-profile" aria-labelledby="links-title">
                    <div className="links-profile-image-wrap">
                        <img
                            className="links-avatar"
                            src={imageUrl('ken-bryn-profile.jpeg')}
                            alt="Ken and Bryn"
                            width="640"
                            height="640"
                            decoding="async"
                            fetchpriority="high"
                        />
                    </div>

                    <div className="links-profile-overlay">
                        <div className="links-profile-copy">
                            <h1 id="links-title">Ken & Bryn</h1>
                            <p className="links-intro">Dancers. Teachers. World Travelers.</p>
                        </div>

                        <section className="social-link-grid" aria-label="Social links">
                            {socialLinks.map((link) => (
                                <a
                                    className="social-link"
                                    href={link.href}
                                    key={link.label}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={link.label}
                                    title={link.label}
                                >
                                    {link.icon}
                                </a>
                            ))}
                        </section>
                    </div>
                </section>
            </div>

            {(upcomingEvents === null || upcomingEvents.length > 0) && (
                <section className="home-events" aria-labelledby="home-events-title" aria-busy={upcomingEvents === null || undefined}>
                    <div className="home-events-heading">
                        <div>
                            <p>On the road</p>
                            <h2 id="home-events-title">Catch us next</h2>
                        </div>
                        <Link to="/events">View all events <FaArrowRight aria-hidden="true" /></Link>
                    </div>

                    <div className="home-event-grid">
                        {upcomingEvents === null
                            ? Array.from({ length: 3 }).map((_, index) => (
                                <div className="home-event-card home-event-skeleton" key={index} aria-hidden="true">
                                    <span /><div><span /><span /></div>
                                </div>
                            ))
                            : upcomingEvents.map((event) => {
                                const date = formatEventDate(event.startDate, event.endDate);
                                const location = formatEventLocation(event.location);
                                return (
                                    <a className="home-event-card" href={event.url} target="_blank" rel="noopener noreferrer" key={event.id}>
                                        <span className="home-event-date" aria-label={`${date.month} ${date.days}`}>
                                            <small>{date.month}</small><strong>{date.days}</strong>
                                        </span>
                                        <span className="home-event-copy">
                                            <strong>{event.name}</strong>
                                            {location && <small>{location}</small>}
                                        </span>
                                        <FaArrowRight aria-hidden="true" />
                                    </a>
                                );
                            })}
                    </div>
                </section>
            )}

            <section className="links-list" aria-label="Featured links">
                {featureLinks.map((link, index) => (
                    <a
                        className={`feature-link${link.description ? ' feature-link-rich' : ''}`}
                        href={link.href}
                        key={link.title}
                        target={link.external ? '_blank' : undefined}
                        rel={link.external ? 'noopener noreferrer' : undefined}
                        style={{ '--link-index': index }}
                    >
                        <span className="feature-link-media" aria-hidden="true">
                            {link.thumbnail ? (
                                <img src={link.thumbnail} alt="" width="66" height="66" loading="lazy" decoding="async" />
                            ) : (
                                <span className="feature-link-icon">{link.icon}</span>
                            )}
                        </span>
                        <span className="feature-link-copy">
                            <span className="feature-link-title">{link.title}</span>
                            {link.description && (
                                <span className="feature-link-description">{link.description}</span>
                            )}
                        </span>
                    </a>
                ))}
            </section>
        </main>
    );
};

export default Home;
